import React, { useState } from 'react';
import cn from 'classnames';
import { ButtonWithIcon } from '@els/els-react--button';
import SummaryActionTooltip from './SummaryActionTooltip';

export const FEEDBACK_STATES = {
  LIKED: 'liked',
  DISLIKED: 'disliked',
};

function ThumbsUpDown({
  thumbsUpProps,
  thumbsDownProps,
  onFeedbackChange,
  ariaLabel = 'Feedback',
  ariaLabelledBy,
  announceLike = 'You liked this.',
  announceDislike = 'You disliked this.',
  thumbsUpTooltip,
  thumbsDownTooltip,
  // FR-4.2: the summary entry passes its persisted `feedbackSubmitted` here so the disabled
  // state survives view switches/stacking/navigation instead of resetting on unmount. Consumers
  // that omit this prop retain the uncontrolled local-state behaviour.
  feedbackState: controlledFeedbackState,
}) {
  const [internalFeedbackState, setInternalFeedbackState] = useState(null);
  const isControlled = controlledFeedbackState !== undefined;
  const feedbackState = isControlled ? controlledFeedbackState : internalFeedbackState;

  const onThumbsUpClick = () => {
    if (!isControlled) {
      setInternalFeedbackState(FEEDBACK_STATES.LIKED);
    }
    if (typeof onFeedbackChange === 'function') {
      onFeedbackChange(true);
    }
  };

  const onThumbsDownClick = () => {
    if (!isControlled) {
      setInternalFeedbackState(FEEDBACK_STATES.DISLIKED);
    }
    if (typeof onFeedbackChange === 'function') {
      onFeedbackChange(false);
    }
  };

  // helper for accessible text
  const feedbackAnnouncement = (state) => {
    if (state === FEEDBACK_STATES.LIKED) return announceLike;
    if (state === FEEDBACK_STATES.DISLIKED) return announceDislike;
    return ''; // empty when nothing chosen
  };

  const thumbsUpButton = (
    <ButtonWithIcon
      data-e2e="summary-like"
      type="link"
      sprite="ThumbsUp"
      iconOnly
      role="radio"
      aria-checked={feedbackState === FEEDBACK_STATES.LIKED}
      className={cn('u-els-margin-right-1x1o2', { 'c-els-button--active-custom': feedbackState === FEEDBACK_STATES.LIKED })}
      onClick={onThumbsUpClick}
      disabled={feedbackState !== null}
      {...thumbsUpProps}>
      Like
    </ButtonWithIcon>
  );

  const thumbsDownButton = (
    <ButtonWithIcon
      data-e2e="summary-dislike"
      type="link"
      sprite="ThumbsDown"
      iconOnly
      role="radio"
      aria-checked={feedbackState === FEEDBACK_STATES.DISLIKED}
      className={cn({ 'c-els-button--active-custom': feedbackState === FEEDBACK_STATES.DISLIKED })}
      onClick={onThumbsDownClick}
      disabled={feedbackState !== null}
      {...thumbsDownProps}>
      Dislike
    </ButtonWithIcon>
  );

  return (
    <>
      <div role="radiogroup" {...(ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : { 'aria-label': ariaLabel })}>
        {thumbsUpTooltip ? <SummaryActionTooltip label={thumbsUpTooltip}>{thumbsUpButton}</SummaryActionTooltip> : thumbsUpButton}
        {thumbsDownTooltip ? <SummaryActionTooltip label={thumbsDownTooltip}>{thumbsDownButton}</SummaryActionTooltip> : thumbsDownButton}
      </div>

      {/* aria-live region — visually hidden but announced by screen readers */}
      <div data-testid="feedback-announcement" aria-live="polite" aria-atomic="true" className="sr-only">
        {feedbackAnnouncement(feedbackState)}
      </div>
    </>
  );
}

export default ThumbsUpDown;
